/**
 * NewsStatsTab.jsx — src/content/Manager/News/NewsStatsTab.jsx
 * Вкладка «Статистика» раздела «Новости»: итоги за период + таблица по новостям.
 */
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Button, Chip, CircularProgress, Option, Select, Sheet, Stack, Table, Typography,
} from '@mui/joy';
import { motion } from 'framer-motion';

import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';

import { LayoutContext } from '../../../context';
import { APIGetNewsStatsSummary } from '../../../api';

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
};

const PERIODS = [
    { value: '7', label: '7 дней' },
    { value: '30', label: '30 дней' },
    { value: '90', label: '90 дней' },
    { value: 'all', label: 'За всё время' },
];

const STATUS_LABELS = { draft: 'Черновик', published: 'Опубликована', archived: 'Архив' };

const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }); }
    catch { return iso; }
};

const fmtSec = (sec) => {
    const value = Number(sec || 0);
    if (value < 60) return `${Math.round(value)} с`;
    const m = Math.floor(value / 60);
    const s = Math.round(value % 60);
    return `${m} мин ${s} с`;
};

const rateColor = (percent) => {
    if (percent >= 70) return '#16a34a';
    if (percent >= 40) return '#eab308';
    return '#ef4444';
};


function TotalCard({ icon, value, label, hint, color = '#6366f1' }) {
    return (
        <Sheet sx={{
            borderRadius: '16px', border: '1px solid', borderColor: 'divider',
            p: 2, display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
            <Box sx={{
                width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                background: `${color}1a`, color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.1 }}>{value}</Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{label}</Typography>
                {hint && <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem' }}>{hint}</Typography>}
            </Box>
        </Sheet>
    );
}


export default function NewsStatsTab() {
    const navigate = useNavigate();
    const { tokenEmployee } = useContext(LayoutContext);

    const [period, setPeriod] = useState('30');
    const [ordering, setOrdering] = useState('-views_total');
    const [data, setData] = useState({ totals: {}, results: [] });
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const params = { ordering };
        if (period !== 'all') {
            params.date_from = daysAgoISO(Number(period));
            params.date_to = todayISO();
        }
        const res = await APIGetNewsStatsSummary(tokenEmployee, params);
        setData(res);
        setLoading(false);
    }, [tokenEmployee, period, ordering]);

    useEffect(() => { load(); }, [load]);

    const totals = data?.totals || {};
    const rows = data?.results || [];

    return (
        <Box>
            {/* Фильтры */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2.5 }}>
                <Select value={period} onChange={(e, v) => v && setPeriod(v)} sx={{ minWidth: 170 }}>
                    {PERIODS.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                </Select>
                <Select value={ordering} onChange={(e, v) => v && setOrdering(v)} sx={{ minWidth: 210 }}>
                    <Option value="-views_total">Сначала популярные</Option>
                    <Option value="-completion_rate">По доле досмотров</Option>
                    <Option value="-avg_duration_sec">По времени просмотра</Option>
                    <Option value="-show_date">По дате показа</Option>
                    <Option value="title">По названию</Option>
                </Select>
            </Stack>

            {/* Итоги */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 1.5, mb: 2.5,
            }}>
                <TotalCard
                    icon={<CampaignRoundedIcon />}
                    value={totals.news_count ?? 0}
                    label="Новостей"
                    hint={`Опубликовано: ${totals.published_count ?? 0}`}
                />
                <TotalCard
                    icon={<GroupRoundedIcon />}
                    value={totals.views_total ?? 0}
                    label="Просмотров"
                    hint="Уникальные пользователи"
                    color="#0ea5e9"
                />
                <TotalCard
                    icon={<DoneAllRoundedIcon />}
                    value={`${totals.completion_rate ?? 0}%`}
                    label="Досмотрели до конца"
                    hint={`${totals.views_completed ?? 0} из ${totals.views_total ?? 0}`}
                    color="#16a34a"
                />
                <TotalCard
                    icon={<TimerRoundedIcon />}
                    value={fmtSec(totals.avg_duration_sec)}
                    label="Среднее время"
                    hint="На одну новость"
                    color="#f59e0b"
                />
            </Box>

            {/* Таблица новостей */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : rows.length === 0 ? (
                <Sheet sx={{ borderRadius: '18px', border: '1px dashed', borderColor: 'divider', p: 6, textAlign: 'center' }}>
                    <InsightsRoundedIcon sx={{ fontSize: 44, color: 'text.tertiary', mb: 1 }} />
                    <Typography sx={{ fontWeight: 800, mb: 0.5 }}>За выбранный период данных нет</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        Опубликуйте новость — статистика появится, как только её кто-нибудь откроет
                    </Typography>
                </Sheet>
            ) : (
                <Sheet
                    component={motion.div}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    sx={{ borderRadius: '18px', border: '1px solid', borderColor: 'divider', overflow: 'auto' }}
                >
                    <Table
                        hoverRow
                        stickyHeader
                        sx={{
                            '--TableCell-headBackground': 'rgba(99,102,241,0.05)',
                            '& thead th': { fontWeight: 800, fontSize: '0.75rem', whiteSpace: 'nowrap' },
                            '& tbody td': { fontSize: '0.85rem' },
                            minWidth: 860,
                        }}
                    >
                        <thead>
                            <tr>
                                <th style={{ width: '30%' }}>Новость</th>
                                <th style={{ width: 110 }}>Дата показа</th>
                                <th style={{ width: 90 }}>Слайдов</th>
                                <th style={{ width: 110 }}>Увидели</th>
                                <th style={{ width: 150 }}>Досмотрели</th>
                                <th style={{ width: 120 }}>Глубина</th>
                                <th style={{ width: 120 }}>Ср. время</th>
                                <th style={{ width: 52 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr
                                    key={row.id}
                                    onClick={() => navigate(`/news/${row.id}/stats`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>
                                        <Stack spacing={0.3}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.86rem' }}>{row.title}</Typography>
                                            <Chip size="sm" variant="soft" sx={{ width: 'fit-content', fontSize: '0.65rem' }}>
                                                {STATUS_LABELS[row.status] || row.status}
                                            </Chip>
                                        </Stack>
                                    </td>
                                    <td>{row.show_mode === 'always' ? 'Всегда' : fmtDate(row.show_date)}</td>
                                    <td>{row.slides_count}</td>
                                    <td><Typography sx={{ fontWeight: 800 }}>{row.views_total}</Typography></td>
                                    <td>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box sx={{ flex: 1, height: 6, borderRadius: 6, background: 'rgba(148,163,184,0.22)', overflow: 'hidden', minWidth: 46 }}>
                                                <Box sx={{
                                                    width: `${Math.min(100, row.completion_rate)}%`,
                                                    height: '100%',
                                                    background: rateColor(row.completion_rate),
                                                }} />
                                            </Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: rateColor(row.completion_rate) }}>
                                                {row.completion_rate}%
                                            </Typography>
                                        </Stack>
                                    </td>
                                    <td>{row.avg_slides_seen} / {row.slides_count}</td>
                                    <td>{fmtSec(row.avg_duration_sec)}</td>
                                    <td><ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'text.tertiary' }} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            )}
        </Box>
    );
}